<!-- src/components/admin/OtaDeploiements.vue -->
<!--
  Déploiement d'un firmware sur un ensemble de cartes, par vagues (06/10/2026,
  backend/services/ota-rollout.service.js). Vague d'essai, délai, vague générale, arrêt
  automatique au seuil d'échecs. Chaque carte suit ensuite le moteur OTA habituel : attente du
  réveil, filet batterie, choix du client, jamais d'installation système armé (sauf urgence).
-->
<template>
  <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
    <div class="px-5 py-3 border-b border-gray-200">
      <h2 class="text-lg font-medium text-gray-900">Déploiements par vagues</h2>
      <p class="text-sm text-gray-500">
        Une version vers un ensemble de cartes : d'abord une <strong>vague d'essai</strong>, puis, après un délai sans échec,
        toutes les autres. Le déploiement <strong>s'arrête tout seul</strong> au seuil d'échecs.
      </p>
    </div>

    <!-- Nouveau déploiement -->
    <form @submit.prevent="lancer" class="px-5 py-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 border-b border-gray-100">
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Version</label>
        <select v-model="f.target_version" class="w-full rounded border-gray-300 text-sm">
          <option value="">Choisir…</option>
          <option v-for="v in versions" :key="v.version" :value="v.version">{{ v.label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Cartes visées</label>
        <select v-model="f.cible_type" class="w-full rounded border-gray-300 text-sm">
          <option value="tous">Tout le parc</option>
          <option value="version">Les cartes d'une version</option>
          <option value="client">Les cartes d'un client</option>
          <option value="appareils">Une sélection</option>
        </select>
      </div>
      <div v-if="f.cible_type === 'version'">
        <label class="block text-xs font-medium text-gray-600 mb-1">Version actuelle des cartes</label>
        <select v-model="f.cible_valeur" class="w-full rounded border-gray-300 text-sm">
          <option v-for="v in versionsParc" :key="v.version" :value="v.version">{{ v.label }} ({{ v.n }})</option>
        </select>
      </div>
      <div v-else-if="f.cible_type === 'client'">
        <label class="block text-xs font-medium text-gray-600 mb-1">Client</label>
        <select v-model="f.cible_valeur" class="w-full rounded border-gray-300 text-sm">
          <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div v-else-if="f.cible_type === 'appareils'" class="sm:col-span-2 lg:col-span-1">
        <label class="block text-xs font-medium text-gray-600 mb-1">Cartes ({{ selection.length }})</label>
        <div class="max-h-32 overflow-y-auto rounded border border-gray-200 p-2 text-xs space-y-1">
          <label v-for="d in devices" :key="d.id" class="flex items-center gap-2">
            <input type="checkbox" :value="d.id" v-model="selection"> {{ d.name }} <span class="text-gray-400">{{ d.client_name }}</span>
          </label>
        </div>
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Vague d'essai (cartes)</label>
        <input v-model.number="f.essai_nombre" type="number" min="0" class="w-full rounded border-gray-300 text-sm">
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Délai avant la vague générale (h)</label>
        <input v-model.number="f.delai_h" type="number" min="0" class="w-full rounded border-gray-300 text-sm">
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Arrêt automatique après (échecs)</label>
        <input v-model.number="f.seuil_echecs" type="number" min="1" class="w-full rounded border-gray-300 text-sm">
      </div>
      <div class="sm:col-span-2 lg:col-span-3 space-y-1">
        <label class="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" v-model="f.imposer">
          Imposer l'installation, même aux clients en mise à jour manuelle (correctif de sécurité ou défaut grave)
        </label>
        <label v-if="f.imposer" class="flex items-center gap-2 text-sm text-red-700 ml-6">
          <input type="checkbox" v-model="f.urgence">
          Urgence : installer même système armé (la carte ne protège plus pendant le redémarrage)
        </label>
        <p v-if="f.imposer" class="text-xs text-gray-500 ml-6">
          Chaque client concerné verra « imposée par le support » dans l'historique et recevra un avis une fois sa carte mise à jour.
        </p>
      </div>
      <div class="sm:col-span-2 lg:col-span-3 flex items-center gap-3 flex-wrap">
        <button type="button" @click="voirApercu" :disabled="!pret" class="btn text-sm">Aperçu</button>
        <button type="submit" :disabled="!pret || envoi" class="btn btn-primary text-sm">Lancer le déploiement</button>
        <span v-if="apercu" class="text-sm text-gray-600">
          {{ apercu.total }} carte(s) visée(s), dont {{ apercu.deja_a_jour }} déjà à jour —
          <span v-for="(n, v) in apercu.par_version" :key="v" class="mr-2">{{ v }} : {{ n }}</span>
        </span>
        <span v-if="erreur" class="text-sm text-red-600">{{ erreur }}</span>
      </div>
    </form>

    <!-- Déploiements -->
    <table class="min-w-full divide-y divide-gray-200 text-sm">
      <thead class="bg-gray-50">
        <tr class="text-left text-gray-500">
          <th class="px-4 py-2 font-medium">#</th>
          <th class="px-4 py-2 font-medium">Version</th>
          <th class="px-4 py-2 font-medium">État</th>
          <th class="px-4 py-2 font-medium">Essai</th>
          <th class="px-4 py-2 font-medium">Vague générale</th>
          <th class="px-4 py-2 font-medium"></th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100">
        <tr v-for="r in deploiements" :key="r.id">
          <td class="px-4 py-2 text-gray-500">{{ r.id }}<div class="text-xs text-gray-400">{{ formatDate(r.created_at) }}</div></td>
          <td class="px-4 py-2 font-medium">{{ r.target_label }}
            <div v-if="r.imposer" class="text-xs text-red-700">imposée{{ r.urgence ? ', urgence' : '' }}</div>
          </td>
          <td class="px-4 py-2">
            <span :class="classeStatut(r.statut)" class="inline-flex px-2 py-0.5 rounded text-xs font-medium">{{ libelleStatut(r) }}</span>
            <div v-if="r.motif" class="text-xs text-red-600 mt-0.5">{{ r.motif }}</div>
            <div v-if="r.generale_prevue && r.statut === 'essai'" class="text-xs text-gray-500 mt-0.5">vague générale le {{ formatDate(r.generale_prevue) }}</div>
          </td>
          <td class="px-4 py-2 text-xs">{{ resume(r.comptes.essai) }}</td>
          <td class="px-4 py-2 text-xs">{{ resume(r.comptes.general) }}</td>
          <td class="px-4 py-2 text-xs whitespace-nowrap">
            <button v-if="r.statut === 'essai'" @click="action(r, 'generale', 'Lancer la vague générale maintenant, sans attendre ?')" class="text-primary-600 font-medium mr-2">Vague générale</button>
            <button v-if="['essai','general'].includes(r.statut)" @click="action(r, 'suspendre', 'Suspendre ce déploiement ? Les campagnes en cours seront annulées.')" class="text-amber-700 font-medium mr-2">Suspendre</button>
            <button v-if="r.statut === 'suspendu'" @click="action(r, 'reprendre', 'Reprendre ce déploiement ?')" class="text-primary-600 font-medium mr-2">Reprendre</button>
            <button v-if="!['termine','annule'].includes(r.statut)" @click="action(r, 'annuler', 'Annuler définitivement ce déploiement ?')" class="text-red-600 font-medium">Annuler</button>
          </td>
        </tr>
        <tr v-if="!deploiements.length"><td colspan="6" class="px-4 py-4 text-center text-gray-400">Aucun déploiement.</td></tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import apiService from '@/services/api.service';
import { formatVersion } from '@/utils/fwVersion';
import { FUSEAU } from '@/i18n/dates';

export default defineComponent({
  name: 'OtaDeploiements',
  props: {
    versions: { type: Array, default: () => [] },   // versions déployables (2 slots)
    devices:  { type: Array, default: () => [] }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const f = reactive({ target_version: '', cible_type: 'tous', cible_valeur: '', essai_nombre: 3, delai_h: 24,
                         seuil_echecs: 1, imposer: false, urgence: false });
    const selection = ref([]), clients = ref([]), deploiements = ref([]), apercu = ref(null), erreur = ref(''), envoi = ref(false);

    const versionsParc = computed(() => {
      const m = {};
      for (const d of props.devices) if (d.fw_ver != null) m[d.fw_ver] = (m[d.fw_ver] || 0) + 1;
      return Object.entries(m).map(([v, n]) => ({ version: Number(v), label: formatVersion(Number(v)), n })).sort((a, b) => b.version - a.version);
    });
    const corps = () => ({ ...f, urgence: f.imposer && f.urgence,
      cible_valeur: f.cible_type === 'appareils' ? selection.value : (f.cible_type === 'tous' ? null : f.cible_valeur) });
    const pret = computed(() => !!f.target_version && (f.cible_type === 'tous' || (f.cible_type === 'appareils' ? selection.value.length > 0 : f.cible_valeur !== '')));

    const charger = async () => {
      try { deploiements.value = (await apiService.listerDeploiements()).data.rollouts || []; } catch { /* rien */ }
    };
    const voirApercu = async () => {
      erreur.value = '';
      try { apercu.value = (await apiService.apercuDeploiement(corps())).data; }
      catch (e) { erreur.value = e.response?.data?.message || 'Aperçu impossible.'; }
    };
    const lancer = async () => {
      erreur.value = '';
      await voirApercu();
      if (!apercu.value) return;
      const v = formatVersion(Number(f.target_version));
      let msg = `Déployer ${v} sur ${apercu.value.total - apercu.value.deja_a_jour} carte(s) ?\n\n`
        + `Vague d'essai : ${f.essai_nombre} carte(s), puis le reste ${f.delai_h} h après un essai sans échec.\n`
        + `Arrêt automatique à ${f.seuil_echecs} échec(s).`;
      if (f.imposer) msg += `\n\n⚠ Installation IMPOSÉE, y compris aux clients en mise à jour manuelle${f.urgence ? ', et MÊME SYSTÈME ARMÉ' : ''}.`;
      if (!confirm(msg)) return;
      envoi.value = true;
      try {
        await apiService.creerDeploiement(corps());
        apercu.value = null; selection.value = [];
        await charger(); emit('change');
      } catch (e) { erreur.value = e.response?.data?.message || 'Lancement impossible.'; }
      finally { envoi.value = false; }
    };
    const action = async (r, a, question) => {
      if (!confirm(question)) return;
      try { await apiService.actionDeploiement(r.id, a); await charger(); emit('change'); }
      catch (e) { alert(e.response?.data?.message || 'Action impossible.'); }
    };

    const resume = c => {
      if (!c || !c.total) return '—';
      const b = [`${c.installees}/${c.total} installée(s)`];
      if (c.en_cours) b.push(`${c.en_cours} en cours`);
      if (c.attente_client) b.push(`${c.attente_client} en attente du client`);
      if (c.en_file) b.push(`${c.en_file} en file`);
      if (c.echecs) b.push(`${c.echecs} échec(s)`);
      if (c.ecartees) b.push(`${c.ecartees} écartée(s)`);
      return b.join(' · ');
    };
    const libelleStatut = r => ({ essai: 'Vague d’essai', general: 'Vague générale', suspendu: 'Suspendu',
                                   termine: 'Terminé', annule: 'Annulé' }[r.statut] || r.statut);
    const classeStatut = s => ({ essai: 'bg-indigo-100 text-indigo-800', general: 'bg-amber-100 text-amber-800',
                                 suspendu: 'bg-red-100 text-red-800', termine: 'bg-green-100 text-green-800',
                                 annule: 'bg-gray-100 text-gray-600' }[s] || 'bg-gray-100 text-gray-600');
    const formatDate = iso => iso ? new Date(String(iso).includes('T') ? iso : String(iso).replace(' ', 'T') + 'Z')
      .toLocaleString('fr-FR', { timeZone: FUSEAU, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

    onMounted(async () => {
      charger();
      try { clients.value = (await apiService.getClients()).data || []; } catch { /* rien */ }
    });
    return { f, selection, clients, deploiements, apercu, erreur, envoi, versionsParc, pret,
             voirApercu, lancer, action, resume, libelleStatut, classeStatut, formatDate, charger };
  }
});
</script>
